import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Clapperboard, IndianRupee, TrendingUp, Users } from 'lucide-react';
import { securitiesAPI, statsAPI } from '../services/api';

const HeroSection = () => {
  const [ipo, setIpo] = useState(null);
  const [stats, setStats] = useState({ total_users: 0, total_securities: 0, total_trades: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ipoRes, statsRes] = await Promise.all([
          securitiesAPI.getIPO(),
          statsAPI.get()
        ]);
        if (ipoRes.data) setIpo(ipoRes.data);
        if (statsRes.data) setStats(statsRes.data);
      } catch {}
    };
    fetchData();
  }, []);

  const imgUrl = ipo?.poster_url || ipo?.photo_url;

  return (
    <section className="relative bg-[#0A0E17] py-16 px-4 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F59E0B] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#F59E0B] rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-full px-4 py-2 mb-6">
              <Clapperboard className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-[#F59E0B] text-sm font-medium">India's #1 Movie Stock Exchange</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-[#F8FAFC] leading-tight mb-6 tracking-tighter">
              Trade the Future of
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#D97706]"> Bollywood</span>
            </h1>

            <p className="text-[#94A3B8] text-lg md:text-xl mb-8 max-w-xl leading-relaxed">
              Buy and sell virtual shares of Indian movies, stars, and studios. Build your portfolio and prove your box office expertise.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link to="/register">
                <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold text-lg px-8 py-6 h-auto rounded-lg" data-testid="hero-signup-btn">
                  <IndianRupee className="w-5 h-5 mr-2" />
                  Get B₹20,00,000 FREE
                </Button>
              </Link>
              <Link to="/securities">
                <Button variant="outline" className="border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#64748B] text-lg px-8 py-6 h-auto rounded-lg" data-testid="hero-browse-btn">
                  Browse Securities
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#F8FAFC] font-mono">{stats.total_users || '0'}+</div>
                <div className="text-[#64748B] text-sm">Active Traders</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#F8FAFC] font-mono">{stats.total_securities || '0'}</div>
                <div className="text-[#64748B] text-sm">Securities</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#F8FAFC] font-mono">{stats.total_trades || '0'}</div>
                <div className="text-[#64748B] text-sm">Total Trades</div>
              </div>
            </div>
          </div>

          {ipo && (
            <div className="relative">
              <div className="bg-[#121826] rounded-2xl p-6 border border-[#1E293B]" data-testid="ipo-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-[#F59E0B] rounded-full flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-black" />
                  </div>
                  <span className="text-[#F59E0B] font-bold uppercase tracking-[0.2em] text-xs">Latest Security</span>
                </div>
                <div className="flex gap-4 mb-4">
                  <div className="w-24 h-36 bg-[#1E293B] rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {imgUrl ? <img src={imgUrl} alt={ipo.name} className="w-full h-full object-cover" /> : <div className="text-3xl font-black text-[#F59E0B]/30 font-mono">{ipo.symbol?.slice(0,3)}</div>}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[#F8FAFC] font-bold text-xl mb-1 font-heading">{ipo.name}</h3>
                    <p className="text-[#64748B] text-sm mb-2">{ipo.security_type === 'movie' ? 'MovieStock' : 'StarBond'}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-[#F59E0B] font-bold text-2xl font-mono">B₹{(ipo.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[#64748B] text-sm mb-4 line-clamp-2">{ipo.description || `Trade ${ipo.name} on Bollywood Sensex`}</p>
                <Link to={`/security/${ipo.symbol}`}>
                  <Button className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="ipo-trade-btn">Trade Now</Button>
                </Link>
              </div>
              <div className="absolute -top-4 -right-4 bg-[#10B981]/10 border border-[#10B981]/20 rounded-lg px-4 py-2 backdrop-blur-sm hidden lg:block">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#10B981]" />
                  <span className="text-[#10B981] font-bold font-mono">+{(ipo.change_percent || 0).toFixed(1)}%</span>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#121826] border border-[#1E293B] rounded-lg px-4 py-2 hidden lg:block">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#F59E0B]" />
                  <span className="text-[#F8FAFC] font-bold font-mono">{stats.total_securities}</span>
                </div>
                <span className="text-[#64748B] text-xs">Securities</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
